import { useOrderDetail } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";

/**
 * /pay-cancel — 支付取消页
 *
 * Design: _design_reference/mini-pay-cancel.html
 * - 渐变黄色背景 (linear-gradient #FFF8DA → #FFE5A0)
 * - ⏸ 暂停图标 (SVG, warning-soft 圆形背景)
 * - badge: "订单已暂存 · 未扣款"
 * - 标题 h1 + 副文案 (含 orderNo 插值)
 * - 订单信息卡: 订单号 · 金额 · 价格锁定倒计时
 * - TgMain 底部 CTA → /confirm-pay (重新支付)
 *
 * orderNo 从 search param (?orderNo=KK-xxx) 读取,
 * 然后用 useOrderDetail 获取真实金额。
 */
export function PayCancelPage() {
  const { t } = useTranslation("pay");
  const authToken = useAuthToken();

  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const params = new URLSearchParams(searchStr.startsWith("?") ? searchStr.slice(1) : searchStr);
  const orderNo = params.get("orderNo") ?? params.get("order_no") ?? undefined;

  const { data: detail } = useOrderDetail(authToken, orderNo ?? "");

  const displayOrderNo = orderNo ?? "—";
  const displayAmount = detail
    ? formatPrice(detail.totalPayAmountRubKopecks, "ru-RU", "RUB")
    : "—";

  return (
    <TgPage tabbar={false}>
      {/* Yellow gradient background — wraps full page per design */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg,#FFF8DA,#FFE5A0)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <TgHeader title={t("cancel.head_title")} back />

      {/* Main centred body */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px",
          textAlign: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* ⏸ Pause icon — SVG rect pair matching web pay-cancel-client.tsx */}
        <div
          style={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            background: "var(--color-warning-soft)",
            color: "var(--color-warning-foreground)",
            display: "grid",
            placeItems: "center",
            marginBottom: 14,
          }}
        >
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden focusable="false">
            <rect x="7" y="5" width="3.5" height="14" rx="1.2" fill="currentColor" />
            <rect x="13.5" y="5" width="3.5" height="14" rx="1.2" fill="currentColor" />
          </svg>
        </div>

        {/* Badge — "订单已暂存 · 未扣款" */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "4px 12px",
            background: "var(--color-warning-soft)",
            color: "var(--color-warning-foreground)",
            border: "1px solid var(--color-sticker-yellow)",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            marginBottom: 12,
          }}
        >
          {t("cancel.badge")}
        </span>

        {/* Title */}
        <h1
          style={{
            fontFamily: "-apple-system,system-ui,sans-serif",
            fontSize: 22,
            fontWeight: 900,
            margin: "0 0 10px",
            letterSpacing: "-0.01em",
          }}
        >
          {t("cancel.head_title")}
        </h1>

        {/* Subtitle with orderNo interpolation */}
        <p
          style={{
            fontSize: 13,
            color: "var(--color-text-muted)",
            maxWidth: 280,
            lineHeight: 1.55,
            margin: "0 0 20px",
          }}
        >
          {t("cancel.subtitle", { orderNo: displayOrderNo })}
        </p>

        {/* Order info card */}
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "14px 18px",
            width: "100%",
            maxWidth: 300,
            textAlign: "left",
          }}
        >
          <InfoRow
            label={t("cancel.info_order_no")}
            value={displayOrderNo}
            mono
          />
          <InfoRow
            label={t("cancel.info_amount")}
            value={displayAmount}
            accent
          />
          <InfoRow
            label={t("cancel.info_status")}
            value={t("cancel.info_status_value")}
          />
        </div>
      </div>

      {/* Bottom CTA — primary: 重新支付 · styled as .tg-main (Link variant) */}
      <Link
        to={"/confirm-pay" as never}
        className="tg-main"
        style={{ textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        {t("cancel.cta_retry")}
      </Link>
    </TgPage>
  );
}

function InfoRow({
  label,
  value,
  mono,
  accent,
}: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span
        style={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          ...(mono ? { fontFamily: "monospace" } : {}),
          ...(accent ? { color: "var(--color-price)" } : {}),
        }}
      >
        {value}
      </span>
    </div>
  );
}
